import React, { useState, useEffect } from 'react'
import Slider from 'react-slick'
import { Typography, Button, Box, useTheme, useMediaQuery } from '@mui/material'
import { styled } from '@mui/material/styles'
import { fetchData } from '../../Assets/API/apiservices'
import {
  IMAGE_BASE_PATH_DBRADC,
  IMAGE_BASE_PATH_COMMON,
  ORGANIZATION_ID,
  SUB_ORGANIZATION_ID,
} from '../../Config/Config'

// Custom styled components
const CarouselContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 0),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 0),
  },
}))

const CarouselTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.common.white,
  padding: theme.spacing(2, 0),
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    padding: theme.spacing(1, 0),
  },
}))

const CarouselItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '500px', // Increased minimum height for better image display
  [theme.breakpoints.down('md')]: {
    minHeight: '400px',
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '350px',
  },
}))

const NoticeImage = styled('img')(({ theme, itemid }) => ({
  width: '90%', // Increased width percentage
  maxWidth: '800px', // Larger maximum width
  height: 'auto',
  maxHeight: '500px', // Increased maximum height
  borderRadius: '20px',
  boxShadow: theme.shadows[6], // Deeper shadow for better depth
  objectFit: 'contain',
  backgroundColor: theme.palette.background.paper,
  margin: theme.spacing(3, 'auto'), // Increased margin
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
  [theme.breakpoints.down('xl')]: {
    maxWidth: '700px',
    maxHeight: '450px',
  },
  [theme.breakpoints.down('lg')]: {
    maxWidth: '600px',
    maxHeight: '400px',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '500px',
    maxHeight: '350px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    maxHeight: '300px',
    margin: theme.spacing(2, 'auto'),
  },
}))

const NoticeCarousel = ({ data }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [noticeData, setNoticeData] = useState([])
  const [expandedIndex, setExpandedIndex] = useState(null)

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000, // Slightly slower autoplay
    adaptiveHeight: true,
    dots: true,
    arrows: false,
    dotsClass: 'slick-dots slick-thumb', // For better dot styling
  }

  useEffect(() => {
    const getActivities = async () => {
      try {
        const response = await fetchData(
          'notices?organization_id=' +
            ORGANIZATION_ID +
            '&sub_organization_id=' +
            SUB_ORGANIZATION_ID
        )
        setNoticeData(response.rows)
      } catch (error) {
        console.error('Error fetching activities:', error)
      }
    }
    getActivities()
  }, [])

  const handleToggleText = (index) => {
    setExpandedIndex(index === expandedIndex ? null : index)
  }

  return (
    <CarouselContainer>
      <CarouselTitle variant="h5" gutterBottom>
        Notice Board
      </CarouselTitle>

      <Slider {...settings}>
        {noticeData.map((item, index) => (
          <CarouselItem key={index}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                color: theme.palette.common.white,
                textAlign: 'center',
                px: isMobile ? 2 : 6,
                mb: 3,
                fontSize: isMobile ? '1rem' : '1.1rem',
                maxWidth: '800px',
              }}
            >
              {expandedIndex === index ? (
                item.description
              ) : (
                <>
                  {item.description.length > 400 ? (
                    <>
                      {item.description.substring(0, isMobile ? 100 : 150)}...{' '}
                      <Button
                        onClick={() => handleToggleText(index)}
                        color="primary"
                        sx={{
                          padding: 0,
                          textTransform: 'none',
                          minWidth: 'auto',
                          fontSize: 'inherit',
                          color: theme.palette.secondary.light,
                          '&:hover': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {expandedIndex === index ? 'Read Less' : 'Read More'}
                      </Button>
                    </>
                  ) : (
                    item.description
                  )}
                </>
              )}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <NoticeImage
                src={IMAGE_BASE_PATH_DBRADC + item.image_url || ''}
                alt="Notice_images"
                itemid={item.id.toString()}
                onError={(e) => {
                  e.target.src =
                    IMAGE_BASE_PATH_COMMON +
                    'Placeholders/Placeholder_Image.jpg'
                }}
              />
            </Box>
          </CarouselItem>
        ))}
      </Slider>
    </CarouselContainer>
  )
}

export default NoticeCarousel
